import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';
import { generateItemLedgerPdf } from './itemLedgerPdf';

// Optional preselect: { scope: 'batch', id, label }
const ItemLedgerModal = ({ preselect = null, onClose }) => {
  const [scope, setScope] = useState(preselect?.scope || 'product');
  const [targetId, setTargetId] = useState(preselect?.id ? String(preselect.id) : '');
  const [fromDate, setFromDate] = useState('2026-01-01');
  const [toDate, setToDate] = useState('');

  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingList(true);
        if (scope === 'product') {
          const res = await chemicalService.getProducts({ page: 1, limit: 1000 });
          setProducts(res.data || []);
        } else {
          const res = await chemicalService.getBatches({ page: 1, limit: 1000 });
          setBatches(res.data || []);
        }
      } catch (err) {
        console.error('Error loading list:', err);
      } finally {
        setLoadingList(false);
      }
    };
    load();
  }, [scope]);

  const handleScopeChange = (newScope) => {
    if (newScope === scope) return;
    setScope(newScope);
    setTargetId('');
  };

  const handleGenerate = async () => {
    if (!targetId) return alert(`❌ Select a ${scope}.`);
    try {
      setGenerating(true);
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const res = await chemicalService.getItemLedger(scope, targetId, params);
      generateItemLedgerPdf(res.data, { from_date: fromDate, to_date: toDate });
      onClose();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || err.message || 'Failed to generate item ledger'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.title}>📘 Item Ledger</h3>
        <p style={styles.sub}>Stock movement history with running balance</p>

        <div style={styles.row}>
          <label style={styles.label}>View By</label>
          <div style={styles.scopeTabs}>
            {[
              { id: 'product', label: 'Product' },
              { id: 'batch', label: 'Batch' },
            ].map((s) => (
              <button
                key={s.id}
                onClick={() => handleScopeChange(s.id)}
                style={{
                  ...styles.scopeButton,
                  background: scope === s.id ? '#e74c3c' : 'white',
                  color: scope === s.id ? 'white' : '#2c3e50',
                  borderColor: scope === s.id ? '#e74c3c' : '#e0e0e0',
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div style={styles.row}>
          <label style={styles.label}>{scope === 'product' ? 'Product' : 'Batch'} *</label>
          <select
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            style={styles.input}
            disabled={loadingList}
          >
            <option value="">
              {loadingList ? 'Loading...' : `-- Select ${scope} --`}
            </option>
            {scope === 'product'
              ? products.map((p) => (
                  <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
                ))
              : batches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.batch_no} · {b.product_name || ''}
                  </option>
                ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{ ...styles.row, flex: 1 }}>
            <label style={styles.label}>From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              onClick={(e) => e.target.showPicker?.()}
              style={styles.input}
            />
          </div>
          <div style={{ ...styles.row, flex: 1 }}>
            <label style={styles.label}>To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              onClick={(e) => e.target.showPicker?.()}
              style={styles.input}
            />
          </div>
        </div>
        <p style={styles.hint}>Leave To Date blank to include everything up to today.</p>

        <div style={styles.actions}>
          <button onClick={onClose} style={styles.cancelButton} disabled={generating}>Cancel</button>
          <button onClick={handleGenerate} style={styles.generateButton} disabled={generating}>
            {generating ? '⏳ Generating...' : '🖨️ Generate PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '12px', padding: '24px',
    width: '100%', maxWidth: '440px', boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
  },
  title: { margin: '0 0 4px', fontSize: '18px', color: '#2c3e50' },
  sub: { margin: '0 0 18px', fontSize: '13px', color: '#95a5a6' },
  row: { marginBottom: '12px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px' },
  scopeTabs: { display: 'flex', gap: '8px' },
  scopeButton: {
    flex: 1, padding: '9px 12px', border: '2px solid',
    borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },
  input: {
    width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0',
    borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit',
    boxSizing: 'border-box', color: '#2c3e50', background: 'white',
    cursor: 'pointer', outline: 'none',
  },
  hint: { fontSize: '12px', color: '#95a5a6', margin: '2px 0 18px' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '10px' },
  cancelButton: {
    padding: '10px 18px', background: 'white', color: '#555',
    border: '2px solid #e0e0e0', borderRadius: '8px', cursor: 'pointer', fontWeight: '600',
  },
  generateButton: {
    padding: '10px 18px', background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700',
  },
};

export default ItemLedgerModal;